import React, { useState, useEffect, useContext } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress"; // ✅ Spinner
import axios from "axios";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import ReportsTable from "./notificationTable";
import { notificationlogsendpoint } from "config";
import { AccountsContext } from "context/AccountsContext";

function NotificationLogs() {
  const [notificationData, setNotificationData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [vehicleId, setVehicleId] = useState("");
  const [loading, setLoading] = useState(false); // ✅ Loading state

  const { fetchThings, things } = useContext(AccountsContext);
  useEffect(() => {
    fetchThings();
  }, []);

  const today = new Date();

  const startTimeDefault = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
  const endTimeDefault = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 0);

  const [startTime, setStartTime] = useState(startTimeDefault);
  const [endTime, setEndTime] = useState(endTimeDefault);

  // Snackbar handlers
  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  // Columns for DataTable
  const columns = [
    { Header: "Timestamp", accessor: "updatedAt", width: "20%" },
    { Header: "Event Type", accessor: "eventType", width: "10%" },
    { Header: "Message", accessor: "message", width: "50%" },
    { Header: "Delivered To", accessor: "toAccount", width: "20%" },
  ];

  const fetchLogs = async () => {
    setLoading(true); // ✅ start loading
    setErrorMessage("");

    try {
      const data = JSON.stringify({
        thingId: vehicleId,
        startTime: new Date(startTime).getTime(),
        endTime: new Date(endTime).getTime(),
      });

      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: notificationlogsendpoint,
        headers: { "Content-Type": "application/json" },
        data,
      };

      const response = await axios.request(config);

      const formattedRows = response.data.map((log) => ({
        updatedAt: new Date(log.updatedAt).toLocaleString("en-GB"),
        eventType: log.eventType,
        message: log.message,
        toAccount: log.toAccount,
      }));

      setNotificationData(formattedRows);
      openSuccessSB();
    } catch (error) {
      console.error(error);
      setErrorMessage("Error loading notification logs");
      openErrorSB();
    } finally {
      setLoading(false); // ✅ stop loading
    }
  };

  const exportToCSV = () => {
    if (notificationData.length === 0) {
      setErrorMessage("No data to export");
      openErrorSB();
      return;
    }

    const headers = ["Timestamp", "Event Type", "Message", "Delivered To"];
    const rows = notificationData.map((row) =>
      [row.updatedAt, row.eventType, `"${row.message}"`, row.toAccount].join(",")
    );
    const csvContent = [headers.join(","), ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `NotificationLogs_${vehicleId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClear = () => {
    setVehicleId("");
    setStartTime(startTimeDefault);
    setEndTime(endTimeDefault);
    setNotificationData([]);
    setErrorMessage("");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} pb={0}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={2}>
                    {/* Vehicle Input */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Autocomplete
                        disablePortal
                        options={things || []}
                        getOptionLabel={(option) => option.thingId}
                        value={things.find((t) => t.thingId === vehicleId) || null}
                        onChange={(event, newValue) => {
                          setVehicleId(newValue ? newValue.thingId : "");
                        }}
                        renderOption={(props, option) => (
                          <li {...props} style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                            <span>{option.nickName}</span>
                            <span style={{ color: "#666" }}>{option.thingId}</span>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Search or add vehicle number" fullWidth size="small" />
                        )}
                      />
                    </Grid>

                    {/* Start Time Picker */}
                    <Grid item xs={12} sm={6} md={3}>
                      <DateTimePicker
                        ampm={false}
                        label="Start Time"
                        value={startTime}
                        onChange={(newValue) => setStartTime(newValue)}
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      />
                    </Grid>

                    {/* End Time Picker */}
                    <Grid item xs={12} sm={6} md={3}>
                      <DateTimePicker
                        ampm={false}
                        label="End Time"
                        value={endTime}
                        onChange={(newValue) => setEndTime(newValue)}
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      />
                    </Grid>

                   
                    <Grid item xs={4} sm={2} md={1}>
                      <MDButton
                        fullWidth
                        variant="gradient"
                        color="info"
                        size="small"
                        onClick={fetchLogs}
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={20} color="inherit" /> : "LOAD"}
                      </MDButton>
                    </Grid>
                    <Grid item xs={4} sm={2} md={1}>
                      <MDButton
                        fullWidth
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleClear}
                        disabled={loading}
                      >
                        CLEAR
                      </MDButton>
                    </Grid>
                    <Grid item xs={4} sm={2} md={1}>
                      <MDButton
                        fullWidth
                        variant="gradient"
                        color="secondary"
                        size="small"
                        onClick={exportToCSV}
                        disabled={loading}
                      >
                        DOWNLOAD
                      </MDButton>
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </MDBox>

              {/* Error alert */}
              {errorMessage && (
                <MDBox px={2} pt={2}>
                  <Alert severity="error">{errorMessage}</Alert>
                </MDBox>
              )}

              {/* Table or loader */}
              <MDBox pt={3} display="flex" justifyContent="center">
                {loading ? (
                  <CircularProgress /> // ✅ center loader while fetching
                ) : (
                  <ReportsTable
                    table={{ columns, rows: notificationData }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Snackbars */}
      <MDSnackbar
        color="success"
        icon="check"
        title="Notification Logs"
        content="Data Loaded Successfully"
        open={successSB}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />
      <MDSnackbar
        color="error"
        icon="warning"
        title="Notification Logs"
        content="Failed to load logs. Please try again."
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default NotificationLogs;
